/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import { getFilesList } from "@Services/dataManager";
import { convertDate } from "@Services/luxonHelper";
import { getReviewUrl } from "@Services/utils/helpers";
import { getViewerUrl, MAX_FILE_UPLOAD_SIZE, formCategories } from "@Helpers";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import FileVersion from ".";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { QueryBuilder, executeQuery } from "@Services/queryBuilder";
import { getStatusBtnStyle } from "@Features/reviews";
import { getFilesGridColumns } from "./FilesGridHelper";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import axiosWrapper from "@Services/axiosWrapper";
import { useDispatch, useSelector } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import type { GetFormTemplateResponse, GetAllUsedFormTemplatesResponse } from "@ServerTypes/projects/project.schema";
import _ from "lodash";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import DropdownButtonComponent from "@CubeComponents/form/DropdownButtonComponentV1";
const TypedDropdownButtonComponent = DropdownButtonComponent as any;

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import {
  setCurrentFolderInFileManager,
  setIsFileVersionUploading,
  setCurrentFileUploadProperties,
  minimiseFileUpload,
  maximiseFileUpload,
  closeFileUpload,
  emptyFileUploadData,
} from "@Actions/fileUpload";

import { useSearchParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import SplitLayout from "@CubeComponents/html/SplitLayout/SplitLayout";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { GridPageLayout, GridPageHeaderLeft } from "@CubeComponents/html/GridPageLayout";
import { RootState } from "@Reducers/index";

interface ShowToolbarOptions {
  views?: boolean;
  group?: boolean;
  upload?: boolean;
  action?: boolean;
  pinnedColumns?: boolean;
}

interface FilesGridProps {
  initialShowAllVersions?: boolean;
  showProjectID?: boolean;
  projectId?: string | null;
  showTitle?: boolean;
  showToolbar?: ShowToolbarOptions;
  propertiesPanel?: boolean;
  excelExport?: boolean;
  handleFileSelectionCallback?: (selectedFiles: any[]) => void;
  spaceId?: string | null;
  showOnlyMergedFile?: boolean;
  disableMenuOptions?: boolean;
  showUpload?: boolean;
  disableFiles?: string[];
}

interface FileData {
  id: string | number;
  fileVersionId?: string;
  fileName: string;
  fileType?: string;
  size?: number;
  createdDate: string;
  createdBy: {
    _id: string;
  };
  file?: {
    space?: {
      id: string;
      project?: {
        id: string;
        projectId: string;
      };
    };
    indexAtProjectLevel: number;
  };
  fileVersionNumber: number;
  isContainer?: boolean;
  resourceType?: string;
  reviews?: any;
  customFields?: Record<string, any>;
  parentFileVersionId?: string | null;
  currentReviewLevel?: any;
  reviewName?: string;
  serialId?: string | number;
  workflow?: string;
  reviewStatusName?: string;
  nextActionDueDate?: string;
  isRowDisabled?: boolean;
}

interface FormTemplate {
  template: any[];
  formTemplateId: string;
}

interface ColumnDefinition {
  field: string;
  id: string;
  width: number;
  originalWidth: number;
  textAlign: string;
  headerText: string;
  _valueGetter?: (args: any) => any;
  name: string;
  columnGroup?: string;
  isReviewColumnType?: boolean;
  [key: string]: any; // Allow additional properties
}

interface FilesGridHeaderProps {
  projectId?: string | null;
  showAllVersions: boolean;
  onToggleVersions: () => void;
}

const FilesGridHeader = memo(function FilesGridHeader({
  projectId,
  showAllVersions,
  onToggleVersions,
}: FilesGridHeaderProps) {
  return (
    <div className="flex items-center gap-4 justify-between pb-4">
      <GridPageHeaderLeft
        icon="FilesIcon"
        title="Files"
        description={
          projectId
            ? "Manage all the files available in the current project"
            : "Manage all the files available in the current portal"
        }
      />
      <div className="flex items-center gap-2">
        <ToggleSwitchComponent checked={showAllVersions} onChange={onToggleVersions} />
        <span>{`Showing ${showAllVersions ? "all" : "only latest"} versions`}</span>
      </div>
    </div>
  );
});

function FilesGrid({
  initialShowAllVersions = true,
  showProjectID = false,
  projectId,
  spaceId,
  showTitle = true,
  showToolbar = { views: true, group: true, upload: true, action: true, pinnedColumns: true },
  propertiesPanel = true,
  excelExport = true,
  handleFileSelectionCallback,
  showOnlyMergedFile,
  disableMenuOptions,
  disableFiles,
}: Readonly<FilesGridProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const byPortal = searchParams.get("byPortal");

  const { fileUploadList, fileUploadState } = useSelector((state: RootState) => state);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [showProperties, setShowProperties] = useState<boolean>(false);
  const [gridReady, setGridReady] = useState<boolean>(false);
  const [originalFilesData, setOriginalFilesData] = useState<FileData[]>([]);
  const [filesData, setFilesData] = useState<FileData[]>([]);
  const [groupActive, setGroupActive] = useState<boolean>(false);
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<string[] | number[]>([]);
  const [customFields, setCustomFields] = useState<Record<string, ColumnDefinition>>({});
  const [showLoader, setShowLoader] = useState<boolean>(true);

  const [layoutDropdown, setLayoutDropdown] = useState<boolean>(false);
  const [showAllVersions, setShowAllVersions] = useState<boolean>(initialShowAllVersions);
  const showAllVersionsRef = useRef<boolean>(initialShowAllVersions);
  const [triggerUpdateCurrentView, setTriggerUpdateCurrentView] = useState<any>(false);
  const [preventUpdate, setPreventUpdate] = useState<boolean>(false);
  const preventUpdateCurrentView = useRef<boolean>(false);
  const [fileVersionId, setFileVersionId] = useState<any[]>([]);
  const formTemplateRef = useRef<FormTemplate>({ template: [], formTemplateId: "" });
  const fileUploadStateRef = useRef<string | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
      layoutDropdown && setLayoutDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    fetchAllUsedFormTemplates();
    if (!byPortal && projectId) {
      getFormTemplate();
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fileUploadStateRef.current = fileUploadState;
    if (fileUploadState === "CLOSED") {
      if (reactGridRef.current?.dataView) {
        loadData(0, 1000000000);
      }
      dispatch(setIsFileVersionUploading(false));
      dispatch(closeFileUpload());
      dispatch(emptyFileUploadData());
    }
  }, [fileUploadState]);

  useEffect(() => {
    if (fileUploadState === "CLOSED") return;
    if (fileUploadList.length) {
      dispatch(minimiseFileUpload());
    } else {
      dispatch(closeFileUpload());
    }
  }, [location, fileUploadList.length, fileUploadState]);

  const handleUpdateCurrentView = (data: any) => {
    if (!preventUpdateCurrentView.current) {
      setTriggerUpdateCurrentView(data);
    }
  };

  useEffect(() => {
    preventUpdateCurrentView.current = preventUpdate;
  }, [preventUpdate]);

  useEffect(() => {
    showAllVersionsRef.current = showAllVersions;
  }, [showAllVersions]);

  // Helper function to filter files to only latest versions
  const filterToLatestVersions = (data: FileData[]): FileData[] => {
    const latestDataMap = new Map<string, FileData>();
    for (const item of data) {
      const projectId = item.file?.space?.project?.projectId;
      const indexAtProjectLevel = item.file?.indexAtProjectLevel;
      if (projectId === undefined || indexAtProjectLevel === undefined) {
        continue;
      }
      const key = `${projectId}-${indexAtProjectLevel}`;
      const existingItem = latestDataMap.get(key);
      if (!existingItem || item.fileVersionNumber > existingItem.fileVersionNumber) {
        latestDataMap.set(key, item);
      }
    }
    return Array.from(latestDataMap.values());
  };

  async function onClickLayout(clickLayout: any): Promise<void> {
    /*
      Both syncronours and asyncronous update is required
    */
    setPreventUpdate(true);
    preventUpdateCurrentView.current = true;
    /*
      end
    */

    const params = clickLayout;

    if (Object.keys(params).length !== 0) {
      // Except "All Files" View
      const { group, pinnedColumns, enableFiltering, enableGrouping, enableAllVersions } = params;

      // The initial saved view resolves before the grid mounts; apply the state parts now
      // (they belong in the same batch as the initial fetch) and replay the grid-instance
      // operations once the grid is ready.
      if (!reactGridRef.current?.slickGrid) {
        pendingLayoutParamsRef.current = params;
        setGroupActive(!!enableGrouping);
        setFilterActive(!!enableFiltering);
        setShowAllVersions(!!enableAllVersions);
        setPreventUpdate(false);
        return;
      }

      if (Array.isArray(group) && group.length) {
        reactGridRef.current.gridState.setGroupConditions(
          group.map((groupItem: any) => ({ column: groupItem, direction: groupItem.direction || "asc" })),
        );
        reactGridRef.current.gridService._grid.invalidate();
      } else {
        reactGridRef.current.slickGrid.setOptions({
          frozenColumn: pinnedColumns,
        });
      }
      setGroupActive(!!enableGrouping);
      setFilterActive(!!enableFiltering);
      setShowAllVersions(!!enableAllVersions);
    }
    setPreventUpdate(false);
  }

  const getFormTemplate = async (): Promise<void> => {
    const response = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplate`, {
      projectId,
      resourceType: formCategories.files,
    });

    if (response.data.status && response.data.data) {
      const formData = response.data.data;
      const template = formData?.template || [];
      formTemplateRef.current = {
        template,
        formTemplateId: formData._id,
      };
    }
  };

  const [filesGridColumns, setFilesGridColumns] = useState<any[]>([]);

  const createColumnFromQuestion = (question: any, sectionHeading: string): ColumnDefinition => {
    const typeMap: Record<string, string> = {
      "single-line": "Text",
      "multi-line": "Text",
      "date-time": "Date",
      "multi-select": "MultiSelect",
    };

    const filterType = typeMap[question.fieldType] || "";

    const columnDef: ColumnDefinition = {
      field: `customFields.${question._id}`,
      id: `${question._id}`,
      width: 150,
      originalWidth: 150,
      textAlign: "Left",
      headerText: question.questionValue,
      filterType: filterType,
      _valueGetter: (args: any) => {
        const value = _.get(args, `customFields.${question._id}`);
        if (question.fieldType === "date-time") {
          if (!value) return null;
          return Number(value);
        }
        return value;
      },
      _excelValueGetter: (args: { customFields?: Record<string, unknown> }) => {
        const value = args.customFields?.[question._id];
        if (value === null || value === undefined || value === "") return "";
        if (question.fieldType === "date-time") return convertDate(Number(value), "dateForTooltip");
        return value;
      },
      name: question.questionValue,
      columnGroup: sectionHeading,
    };

    // Add formatter for date-time fields to display formatted date
    if (question.fieldType === "date-time") {
      (columnDef as any).formatter = (_row: any, _cell: any, _value: any, _columnDef: any, dataContext: any) => {
        const value = _.get(dataContext, `customFields.${question._id}`);
        if (!value) return "";
        return convertDate(Number(value), "dateForText");
      };
    }

    return columnDef;
  };

  const processQuestionFields = (
    questionFields: any[],
    sectionHeading: string,
    extraColumns: Record<string, ColumnDefinition>,
  ): void => {
    if (!questionFields) return;
    for (const question of questionFields) {
      extraColumns[question._id] = createColumnFromQuestion(question, sectionHeading);
    }
  };

  const processTemplate = (template: any[], extraColumns: Record<string, ColumnDefinition>): void => {
    if (!template) return;
    for (const section of template) {
      const { questionFields, sectionHeading } = section;
      processQuestionFields(questionFields, sectionHeading, extraColumns);
    }
  };

  const processTemplatesData = (data: any[]): Record<string, ColumnDefinition> => {
    const extraColumns: Record<string, ColumnDefinition> = {};
    for (const item of data) {
      const { template } = item;
      processTemplate(template, extraColumns);
    }
    return extraColumns;
  };

  const fetchAllUsedFormTemplates = async (): Promise<void> => {
    let extraColumns: Record<string, ColumnDefinition> = {};

    if (projectId) {
      const response = await axiosWrapper.post<GetAllUsedFormTemplatesResponse>(
        `${getBackendUrl()}/projects/getAllUsedFormTemplates`,
        { projectId },
      );
      extraColumns = processTemplatesData(response.data.data ?? []);
      setCustomFields(extraColumns);
    }

    const allColumns = getFilesGridColumns({
      userAvatar,
      formatDate,
      statusInReviewTemplate,
      currentReviewLevelFormat,
      // actionFormatter,
      showProjectID,
      extraColumns,
      getReviewStatus,
      reactGridRef,
    });

    setFilesGridColumns(allColumns);
  };

  const formatDate = (params: FileData): HTMLDivElement => {
    const dateFormat = convertDate(Number(params.createdDate), "dateForTooltip");
    const container = document.createElement("div");
    const root = createRoot(container);

    root.render(
      <ToolTipComponent position="bottom" content={dateFormat}>
        {convertDate(Number(params.createdDate), "dateForText")}
      </ToolTipComponent>,
    );

    return container;
  };

  const userAvatar = (args: FileData): HTMLDivElement => {
    const container = document.createElement("div");
    renderUserTag(container, { id: args.createdBy._id });
    return container;
  };

  const getReviewStatus = (args: any): string => {
    const status = _.get(args, "reviews.reviewStatus");
    const currentReviewLevel = args.currentReviewLevel;
    if (currentReviewLevel === "Varies" || currentReviewLevel === "") {
      return currentReviewLevel;
    }
    if (currentReviewLevel) {
      return currentReviewLevel.title;
    }
    if (status === "Closed") {
      return "Review Closed";
    } else if (status === "Revoked") {
      return "Review Revoked";
    } else if (status === "Draft") {
      return "Review Draft";
    } else if (status === "Open") {
      return "Review Open";
    }
    return "";
  };

  const currentReviewLevelFormat = (value: any): HTMLDivElement => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(<span className="italic text-gray-500">{getReviewStatus(value)}</span>);
    return container;
  };

  const statusInReviewTemplate = (args: any): string | HTMLDivElement => {
    if (!_.get(args, "reviews.status.name")) {
      return "-";
    }

    const container = document.createElement("div");
    const root = createRoot(container);

    const { name, category } = args.reviews.status;
    const style = getStatusBtnStyle(category);

    root.render(
      <p className="w-fit px-2 py-1 rounded-lg text-white font-semibold text-xs" style={style}>
        {name}
      </p>,
    );
    return container;
  };

  const handleFileOpenClick = (params: FileData): void => {
    const { fileVersionId, file } = params;
    const url = getViewerUrl({
      fileVersionId: fileVersionId || "",
      spaceId: _.get(file, "space.id"),
      projectId: (_.get(file, "space.project.id") || projectId) ?? "",
    });

    window.open(url, "_blank");
  };

  const handleReviewOpenClick = (_e: any, args: any): void => {
    const spaceId = args.file.space.id;
    const projectId = args.file.space.project.id;
    const reviewId = args.reviews._id;
    if (reviewId) {
      const url = getReviewUrl(projectId, spaceId, reviewId);
      window.open(url, "_blank");
    }
  };

  const handleOpenResource = (_e: any, params: FileData): void => {
    if (params.isContainer) {
      dispatch(
        showToastNotification({
          content: t("Cannot open container"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      handleFileOpenClick(params);
    }
  };

  const handleViewProperties = async (): Promise<void> => {
    const args = reactGridRef.current.dataView.getAllSelectedItems();
    const fileVersionIds = args.map((data: FileData) => data.fileVersionId);
    setFileVersionId(fileVersionIds);
  };

  useEffect(() => {
    if (showProperties && selectedRowId) {
      handleViewProperties();
    }
  }, [showProperties, selectedRowId]);

  const handlePropertiesOpen = (): void => {
    const next = !showProperties;
    setShowProperties(next);
    if (!next) {
      setFileVersionId([]);
    }
  };

  const handleActionClick = (data: number): void => {
    const args = reactGridRef.current.dataView.getAllSelectedItems()[0];
    if (data === 1) {
      handleOpenResource(undefined, args);
    } else if (data === 2) {
      handleReviewOpenClick(undefined, args);
    }
  };

  const handleRowSelection = (args: any): void => {
    const selectedData = reactGridRef.current.slickGrid
      .getSelectedRows()
      .map((data: number) => reactGridRef.current.slickGrid.getDataItem(data));
    const filteredData = selectedData.filter((file: FileData) => !!file && !file.isRowDisabled);
    handleFileSelectionCallback?.(
      filteredData.map((file: any) => {
        return {
          id: file._id,
          hierarchyId: file._id,
          name: file.fileName,
          userId: file.createdBy._id,
          dateCreated: file.createdDate,
          versionNumber: file.fileVersionNumber,
          fileVersion: file.fileVersionId,
          size: file.size,
          conversionStatus: file.conversionStatus,
          isContainer: file.isContainer,
          parentId: file.parentId,
          resourceType: file.resourceType,
          project: file.file.space.project,
          space: file.space,
          spaceId: file.spaceId,
          fileType: file.fileType,
          engineType: file.engineType,
          isFile: file.isFile,
        };
      }),
    );

    setSelectedRowId(args.detail.args.ids);
  };

  const loadData = async (skip: number, take: number): Promise<void> => {
    // setShowLoader(true);

    const response: any = await fetchFilesData(skip, take);
    const reviewsByVersionId = mapReviewsByVersionId(response.reviewsData);
    const fullResponse = buildFilesWithReviews(response.result, reviewsByVersionId);
    const modifiedResponse =
      Array.isArray(disableFiles) && disableFiles.length
        ? fullResponse.map((file: FileData) =>
            file.fileVersionId && disableFiles.includes(file.fileVersionId) ? { ...file, isRowDisabled: true } : file,
          )
        : fullResponse;

    setOriginalFilesData(modifiedResponse);

    // Apply version filtering based on showAllVersions ref (use ref to avoid stale closure)
    if (showAllVersionsRef.current) {
      setFilesData(modifiedResponse);
    } else {
      setFilesData(filterToLatestVersions(modifiedResponse));
    }

    setShowLoader(false);

    // Force grid to re-render cells after data loads to ensure formatters/valueGetters are applied
    requestAnimationFrame(() => {
      if (reactGridRef.current?.slickGrid) {
        reactGridRef.current.slickGrid.invalidate();
      }
    });
  };

  // Fetch files from service
  const fetchFilesData = async (skip: number, take: number) => {
    const queryData = new QueryBuilder()
      .where("parentFileVersionId", "equal", null)
      .requiresCount()
      .skip(skip)
      .take(take);

    if (projectId) {
      queryData.addParams("projectId", projectId);
    }

    if (spaceId) {
      queryData.addParams("spaceId", spaceId);
    }

    if (showOnlyMergedFile) {
      queryData.addParams("showOnlyMergedFile", showOnlyMergedFile);
    }

    return await executeQuery(getFilesList, queryData);
  };

  const mapReviewsByVersionId = (reviewsData: any[]): Map<string, any[]> => {
    const map = new Map<string, any[]>();
    for (const review of reviewsData) {
      for (const { fileVersionId, status } of review.fileVersionStatuses) {
        if (!fileVersionId) continue;
        const reviews = map.get(fileVersionId) || [];
        reviews.push({
          _id: review._id,
          createdDate: review.createdDate,
          name: review.name,
          workflow: review.workflow,
          status,
          serialId: review.serialId,
          currentReviewLevel: review.currentReviewLevel,
          nextActionDueDate: review.nextActionDueDate,
          reviewStatus: review.reviewStatus,
          initiationDate: review.initiationDate,
          finishedDate: review.finishedDate,
        });
        map.set(fileVersionId, reviews);
      }
    }
    return map;
  };

  // Build files array with reviews
  const buildFilesWithReviews = (filesData: any[], reviewsByVersionId: Map<string, any[]>) => {
    const result: FileData[] = [];

    for (const data of filesData) {
      setFileType(data);

      const reviews = reviewsByVersionId.get(data.id?.toString()) || [];

      if (reviews.length > 0) {
        for (const [index, review] of reviews.entries()) {
          result.push({
            ...data,
            reviews: review,
            id: `${data.id}_${index}`,
            reviewName: review.name,
            serialId: review.serialId,
            workflow: review.workflow.name,
            currentReviewLevel: review.currentReviewLevel,
            reviewStatusName: review.status.name,
            nextActionDueDate: review.nextActionDueDate,
          });
        }
      } else {
        result.push({ ...data, reviews: {} });
      }
    }

    return result;
  };

  // Helper: set fileType
  const setFileType = (data: any) => {
    if (data.resourceType === "RESOURCE_TYPE_FILE") {
      const fileName = data.fileName;
      data.fileType = fileName.includes(".") ? fileName.substring(fileName.lastIndexOf(".") + 1).toUpperCase() : "";
    } else {
      data.fileType = "";
    }
  };

  useEffect(() => {
    loadData(0, 1000000000);
  }, []);

  const reactGridRef = useRef<any>(null);
  const pendingLayoutParamsRef = useRef<any>(null);

  useEffect(() => {
    if (gridReady && pendingLayoutParamsRef.current) {
      const pendingParams = pendingLayoutParamsRef.current;
      pendingLayoutParamsRef.current = null;
      // Replay after the grid's own view application (it runs in the grid-mounted commit),
      // matching the old ordering where this handler ran after the grid applied the view.
      // suppressViewUpdates keeps the replayed state changes from re-persisting the view.
      setTimeout(() => {
        const grid = reactGridRef.current;
        if (!grid?.slickGrid) return;
        if (grid.suppressViewUpdates) {
          grid.suppressViewUpdates(() => onClickLayout(pendingParams));
        } else {
          onClickLayout(pendingParams);
        }
      }, 0);
    }
  }, [gridReady]);

  useEffect(() => {
    if (gridReady) {
      handleUpdateCurrentView({
        enableGrouping: groupActive,
      });
    }
  }, [gridReady, groupActive]);

  useEffect(() => {
    handleUpdateCurrentView({
      enableAllVersions: showAllVersions,
    });
  }, [showAllVersions]);

  const handleFileUpload = (): void => {
    if (fileUploadState === "MINIMISED") {
      dispatch(
        showToastNotification({
          content: t("Another upload is in progress. Please close that popup to proceed."),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }

    dispatch(
      setCurrentFileUploadProperties({
        fileAllowedExtensions: {
          minFileSize: 0,
          maxFileSize: MAX_FILE_UPLOAD_SIZE,
          allowedExtensions: "",
        },
        isUploadAllowed: true,
        // getPermission,
        formTemplate: formTemplateRef.current,
      }),
    );
    dispatch(setCurrentFolderInFileManager({ hierarchyId: "0" }));
    dispatch(maximiseFileUpload());
  };

  const handleGroupActive = (): void => {
    setGroupActive(!groupActive);
  };

  const handleFilterActive = (): void => {
    setFilterActive(!filterActive);
  };

  const handleShowAllVersions = useCallback((): void => {
    setShowAllVersions((prev) => {
      if (prev) {
        setFilesData(filterToLatestVersions(originalFilesData));
      } else {
        setFilesData(originalFilesData);
      }
      return !prev;
    });
  }, [originalFilesData]);

  const updateItemData = (
    item: FileData,
    fileVersionIdToMatch: string[] | null,
    dataKey: string,
    dataValue: any,
  ): boolean => {
    if (!fileVersionIdToMatch || !Array.isArray(fileVersionIdToMatch)) return false;
    if (!fileVersionIdToMatch.includes(String(item.id))) return false;
    _.update(item, dataKey, () => dataValue);
    return true;
  };

  const processDataKey = (dataKey: string, data: any, items: FileData[], fileVersionId: string[] | null): boolean => {
    return items.some((item: FileData) => updateItemData(item, fileVersionId, dataKey, data[dataKey]));
  };

  const onChange = (data: any): void => {
    const { dataView, slickGrid } = reactGridRef.current;
    const items = dataView.items;

    for (const dataKey of Object.keys(data)) {
      processDataKey(dataKey, data, items, fileVersionId);
    }

    slickGrid.invalidate();
  };

  const actionTemplate = (): React.ReactElement => {
    return (
      <TypedDropdownButtonComponent
        disabled={selectedRowId?.length !== 1}
        // buttonType={BUTTON_TYPES.DROPDOWN_BACKGROUND_BUTTON}
        content={t("Action")}
        select={handleActionClick}
        items={[
          {
            text: t("Open File"),
            id: 1,
          },
          {
            text: t("Open Review"),
            id: 2,
          },
        ]}
      />
    );
  };

  const toolbarClickHandler = (args: string): void => {
    if (args === "upload") {
      handleFileUpload();
    } else if (args === "group") {
      handleGroupActive();
    } else if (args === "filter") {
      handleFilterActive();
    }
    if (args === "properties") {
      handlePropertiesOpen();
    }
  };

  const toolbar: any[] = [
    showToolbar.upload && {
      name: t("Upload"),
      id: "upload",
      buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
    },
    showToolbar.action && {
      id: "action",
      template: actionTemplate,
    },
  ].filter(Boolean);

  const [enableWrapText, setEnableWrapText] = useState<boolean>(false);

  return (
    <div className="flex gap-4 h-full w-full">
      <SplitLayout
        initialSizes={showProperties ? [66, 34] : [100, 0]}
        gutterWidth={"0.25rem"}
        minSizes={[50, 20]}
        style={{ display: "flex", height: "100%" }}
        showProperties={showProperties}
      >
        <GridPageLayout
          showHeader={showTitle}
          customHeader={
            <FilesGridHeader
              projectId={projectId}
              showAllVersions={showAllVersions}
              onToggleVersions={handleShowAllVersions}
            />
          }
          className="min-w-0"
          gridContainerClassName="items-center justify-center overflow-auto"
        >
          {filesGridColumns.length > 0 && (
            <>
              {showLoader ? (
                <CircularProgress />
              ) : (
                <SlickGrid1
                  columnDefinitions={filesGridColumns}
                  enableWrapText={enableWrapText}
                  setEnableWrapText={setEnableWrapText}
                  gridOptions={{
                    showPreHeaderPanel: Object.keys(customFields).length > 0,
                    enableAutoSizeColumns: false,
                  }}
                  onClickLayout={onClickLayout}
                  onSelectedRowIdsChanged={handleRowSelection}
                  setGridReady={setGridReady}
                  triggerUpdateCurrentView={triggerUpdateCurrentView}
                  dataset={filesData}
                  enableGroupingOption
                  enableFilteringOption
                  enableSearchBar
                  {...(projectId &&
                    showToolbar.views && {
                      viewsOptions: {
                        headerName: "Views",
                        name: "View",
                        resourceType: "FILES",
                        selectedLayoutKey: "FILES_SELECTED_LAYOUT",
                        fetchParams: {
                          projectId,
                        },
                      },
                    })}
                  enableViews={!!(projectId && showToolbar.views)}
                  disableMenuOptions={disableMenuOptions}
                  enableSortingOption
                  enableColumnChooser
                  enableRowResize={true}
                  toolbar={toolbar}
                  handleUpdateCurrentView={handleUpdateCurrentView}
                  ref={reactGridRef}
                  excelExport={excelExport}
                  toolbarClickHandler={toolbarClickHandler}
                  propertiesPanel={propertiesPanel}
                />
              )}
            </>
          )}
        </GridPageLayout>
        {showProperties && (
          <div className="w-full h-full">
            <FileVersion
              selectedSpaceTypeInFileManager="this"
              fileVersionId={fileVersionId as any}
              isContainer={reactGridRef.current.dataView.getAllSelectedItems()[0]?.isContainer}
              onChange={onChange}
              disableEngineType={false}
            />
          </div>
        )}
      </SplitLayout>
    </div>
  );
}

export default FilesGrid;
